'use client';

import React, { useEffect, useRef, useState } from 'react';

const TIERS = [
  {
    id: 'foundation',
    name: 'Foundation',
    subtitle: 'Get industry-ready',
    price: '£495',
    duration: '3 days',
    tag: null,
    color: 'rgba(244,246,248,0.08)',
    borderColor: 'rgba(244,246,248,0.1)',
    textAccent: '#F4F6F8',
  },
  {
    id: 'intermediate',
    name: 'Intermediate',
    subtitle: 'Break into broadcast',
    price: '£895',
    duration: '5 days',
    tag: 'Most Popular',
    color: 'rgba(244,246,248,0.05)',
    borderColor: 'rgba(244,246,248,0.12)',
    textAccent: '#F4F6F8',
  },
  {
    id: 'master',
    name: 'Master',
    subtitle: 'Direct & lead',
    price: '£1,595',
    duration: '10 days',
    tag: 'Best Value',
    color: 'rgba(0,230,118,0.04)',
    borderColor: 'rgba(0,230,118,0.22)',
    textAccent: '#00E676',
  },
];

const FEATURES = [
  { label: 'Professional-grade workstation access', foundation: true, intermediate: true, master: true },
  { label: 'Avid Media Composer suite', foundation: true, intermediate: true, master: true },
  { label: 'DaVinci Resolve color bay', foundation: false, intermediate: true, master: true },
  { label: 'Pro Tools sound stage', foundation: false, intermediate: true, master: true },
  { label: 'Broadcast standards certification', foundation: false, intermediate: true, master: true },
  { label: 'Directing for camera workshop', foundation: false, intermediate: false, master: true },
  { label: '1-to-1 mentor sessions', foundation: false, intermediate: false, master: true },
  { label: 'Agency placement support', foundation: false, intermediate: false, master: true },
  { label: 'Lifetime alumni network access', foundation: false, intermediate: true, master: true },
  { label: 'Portfolio review with industry lead', foundation: false, intermediate: false, master: true },
  { label: 'Free module trial (no card)', foundation: true, intermediate: true, master: true },
];

function CheckIcon({ active }: { active: boolean }) {
  if (active) {
    return (
      <div
        className="w-6 h-6 rounded-full flex items-center justify-center mx-auto"
        style={{ background: 'rgba(0,230,118,0.12)' }}
      >
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#00E676" strokeWidth="3">
          <path d="M20 6L9 17l-5-5" />
        </svg>
      </div>
    );
  }
  return (
    <div className="w-4 h-px mx-auto" style={{ background: 'rgba(244,246,248,0.15)' }} />
  );
}

export default function ComparisonTable() {
  const sectionRef = useRef<HTMLElement>(null);
  const rowRefs = useRef<(HTMLTableRowElement | null)[]>([]);
  const [headerVisible, setHeaderVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('revealed');
          }
        });
      },
      { threshold: 0.15 }
    );

    const headerEl = sectionRef.current?.querySelector('.section-header');
    if (headerEl) {
      headerEl.classList.add('reveal-hidden');
      observer.observe(headerEl);
    }

    rowRefs.current.forEach((row, i) => {
      if (row) {
        row.classList.add('table-row-hidden');
        row.style.transitionDelay = `${i * 60}ms`;
        observer.observe(row);
      }
    });

    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="bootcamps"
      ref={sectionRef}
      className="py-24 px-6 relative overflow-hidden"
      style={{ background: 'linear-gradient(180deg, #0B1A2E 0%, #0A1828 100%)' }}
    >
      {/* Background accent */}
      <div
        className="absolute pointer-events-none"
        style={{
          top: '0',
          left: '50%',
          transform: 'translateX(-50%)',
          width: '800px',
          height: '400px',
          background: 'radial-gradient(ellipse, rgba(0,230,118,0.03) 0%, transparent 70%)',
        }}
      />

      <div className="max-w-6xl mx-auto relative z-10">
        {/* Header */}
        <div className="section-header text-center mb-16">
          <div
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-5 text-xs font-semibold tracking-widest uppercase"
            style={{ background: 'rgba(0,230,118,0.08)', border: '1px solid rgba(0,230,118,0.15)', color: '#00E676' }}
          >
            Three Tiers. One Standard.
          </div>
          <h2
            className="font-display font-light leading-none tracking-tight mb-4"
            style={{ fontSize: 'clamp(2.2rem, 5vw, 3.8rem)', color: '#F4F6F8' }}
          >
            Choose your{' '}
            <em style={{ color: 'rgba(244,246,248,0.5)' }}>threshold.</em>
          </h2>
          <p
            className="text-base font-light max-w-xl mx-auto"
            style={{ color: 'rgba(244,246,248,0.5)' }}
          >
            Every tier runs in a real post-production environment. No simulations.
            No slideware. The gap between where you are and broadcast standard closes fast.
          </p>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full" style={{ borderCollapse: 'separate', borderSpacing: '0 0' }}>
            {/* Tier headers */}
            <thead>
              <tr>
                <th className="w-64 pb-6 text-left pr-6">
                  <span
                    className="text-xs font-semibold tracking-widest uppercase"
                    style={{ color: 'rgba(244,246,248,0.3)' }}
                  >
                    Curriculum Features
                  </span>
                </th>
                {TIERS.map((tier) => (
                  <th
                    key={tier.id}
                    className="pb-6 px-4 text-center align-top"
                    style={{ minWidth: '180px' }}
                  >
                    <div
                      className="rounded-2xl p-5 relative overflow-hidden"
                      style={{
                        background: tier.color,
                        border: `1px solid ${tier.borderColor}`,
                      }}
                    >
                      {tier.tag && (
                        <div
                          className="absolute top-3 right-3 px-2 py-0.5 rounded-full text-xs font-semibold"
                          style={{
                            background: tier.id === 'master' ? '#00E676' : 'rgba(244,246,248,0.1)',
                            color: tier.id === 'master' ? '#0B1A2E' : '#F4F6F8',
                          }}
                        >
                          {tier.tag}
                        </div>
                      )}
                      <div
                        className="text-xs font-semibold tracking-widest uppercase mb-1"
                        style={{ color: tier.textAccent, opacity: 0.6 }}
                      >
                        {tier.name}
                      </div>
                      <div
                        className="font-display font-light text-2xl mb-0.5"
                        style={{ color: tier.textAccent }}
                      >
                        {tier.price}
                      </div>
                      <div className="text-xs" style={{ color: 'rgba(244,246,248,0.4)' }}>
                        {tier.duration} intensive
                      </div>
                      <div className="text-xs mt-1" style={{ color: 'rgba(244,246,248,0.5)' }}>
                        {tier.subtitle}
                      </div>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>

            {/* Feature rows */}
            <tbody>
              {FEATURES.map((feature, i) => (
                <tr
                  key={feature.label}
                  ref={(el) => { rowRefs.current[i] = el; }}
                  style={{
                    borderBottom: '1px solid rgba(244,246,248,0.05)',
                  }}
                >
                  <td className="py-4 pr-6 text-sm" style={{ color: 'rgba(244,246,248,0.65)' }}>
                    {feature.label}
                  </td>
                  {TIERS.map((tier) => (
                    <td key={tier.id} className="py-4 px-4 text-center">
                      <CheckIcon
                        active={
                          tier.id === 'foundation'
                            ? feature.foundation
                            : tier.id === 'intermediate'
                            ? feature.intermediate
                            : feature.master
                        }
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pinned CTA below table */}
        <div
          className="mt-10 flex flex-col sm:flex-row items-center justify-between gap-6 p-6 rounded-2xl"
          style={{
            background: 'rgba(0,230,118,0.05)',
            border: '1px solid rgba(0,230,118,0.15)',
          }}
        >
          <div>
            <p className="font-semibold text-base mb-1" style={{ color: '#F4F6F8' }}>
              Not sure which tier? Start free.
            </p>
            <p className="text-sm" style={{ color: 'rgba(244,246,248,0.5)' }}>
              Your free module runs in a real Avid suite. No credit card. No commitment language.
            </p>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            <a
              href="#"
              className="text-sm font-medium transition-colors duration-200 whitespace-nowrap"
              style={{ color: 'rgba(244,246,248,0.5)' }}
              onMouseEnter={(e) => (e.currentTarget.style.color = '#F4F6F8')}
              onMouseLeave={(e) => (e.currentTarget.style.color = 'rgba(244,246,248,0.5)')}
            >
              Download Syllabus →
            </a>
            <a
              href="#signup"
              className="btn-magnetic pulse-green inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold whitespace-nowrap"
              style={{ background: '#00E676', color: '#0B1A2E' }}
            >
              <span className="blink w-2 h-2 rounded-full" style={{ background: '#0B1A2E', opacity: 0.7 }} />
              Start Your Free Module
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}